import {
  Controller,
  Post,
  Get,
  Put,
  Delete,
  Body,
  Param,
  Query,
  Req,
  HttpCode,
  HttpStatus,
  UseInterceptors,
  UploadedFile,
} from '@nestjs/common';
import {
  ApiTags,
  ApiOperation,
  ApiResponse,
  ApiQuery,
  ApiConsumes,
} from '@nestjs/swagger';
import { FastifyRequest } from 'fastify';
import { UserService } from '../services/user.service';
import { SignupDto } from '../dto/signup.dto';
import { LoginDto } from '../dto/login.dto';
import { SocialLoginDto } from '../dto/social-login.dto';
import { ForgotPasswordDto } from '../dto/forgot-password.dto';
import { ChangePasswordDto } from '../dto/change-password.dto';
import { UpdateUserDto } from '../dto/update-user.dto';
import { UserResponseDto } from '../dto/user-response.dto';
import { Public } from '../../../common/decorators/public.decorator';
import { ApiSuccessResponse } from '@/common/types/api-response';
import {
  FileInterceptor,
  MulterFile,
} from '@webundsoehne/nest-fastify-file-upload';

@ApiTags('Authentication & Users')
@Controller('auth')
export class UserController {
  constructor(private readonly userService: UserService) {}

  @Post('signup')
  @Public()
  @UseInterceptors(FileInterceptor('image_url'))
  @ApiConsumes('multipart/form-data')
  @ApiOperation({ summary: 'User signup' })
  @ApiResponse({
    status: 201,
    description: 'User created successfully',
    type: UserResponseDto,
  })
  @ApiResponse({ status: 409, description: 'User already exists' })
  async signup(
    @Body() signupDto: SignupDto,
    @UploadedFile() image_url?: MulterFile,
  ) {
    return this.userService.signup({
      ...signupDto,
      image_url,
    });
  }

  @Post('login')
  @Public()
  @HttpCode(HttpStatus.OK)
  @ApiOperation({ summary: 'User login' })
  @ApiResponse({
    status: 200,
    description: 'Login successful',
    type: UserResponseDto,
  })
  @ApiResponse({ status: 401, description: 'Invalid credentials' })
  async login(
    @Body() loginDto: LoginDto,
  ): Promise<ApiSuccessResponse<UserResponseDto>> {
    return this.userService.login(loginDto);
  }

  @Post('social-login')
  @Public()
  @HttpCode(HttpStatus.OK)
  @ApiOperation({ summary: 'Social login (Google, Apple, etc.)' })
  @ApiResponse({
    status: 200,
    description: 'Social login successful',
    type: UserResponseDto,
  })
  @ApiResponse({ status: 400, description: 'Invalid social login data' })
  async socialLogin(
    @Body() socialLoginDto: SocialLoginDto,
  ): Promise<ApiSuccessResponse<UserResponseDto>> {
    return this.userService.socialLogin(socialLoginDto);
  }

  @Get('verify-email/:token')
  @Public()
  @ApiOperation({ summary: 'Verify email address' })
  @ApiResponse({ status: 200, description: 'Email verified successfully' })
  @ApiResponse({ status: 404, description: 'Invalid verification token' })
  async verifyEmail(@Param('token') token: string) {
    return this.userService.verifyEmail(token);
  }

  @Post('resend-verification')
  @Public()
  @HttpCode(HttpStatus.OK)
  @ApiOperation({ summary: 'Resend email verification' })
  @ApiResponse({ status: 200, description: 'Verification email sent' })
  @ApiResponse({ status: 404, description: 'User not found' })
  async resendVerificationEmail(@Body('email') email: string) {
    return this.userService.resendVerificationEmail(email);
  }

  @Post('forgot-password')
  @Public()
  @HttpCode(HttpStatus.OK)
  @ApiOperation({ summary: 'Request password reset' })
  @ApiResponse({ status: 200, description: 'Password reset email sent' })
  async forgotPassword(@Body() forgotPasswordDto: ForgotPasswordDto) {
    return this.userService.forgotPassword(forgotPasswordDto.email);
  }

  @Post('reset-password/:token')
  @Public()
  @HttpCode(HttpStatus.OK)
  @ApiOperation({ summary: 'Reset password with token' })
  @ApiResponse({ status: 200, description: 'Password reset successfully' })
  @ApiResponse({ status: 400, description: 'Invalid or expired token' })
  async resetPassword(
    @Param('token') token: string,
    @Body('password') password: string,
  ) {
    return this.userService.resetPassword(token, password);
  }

  @Post('change-password')
  @HttpCode(HttpStatus.OK)
  @ApiOperation({ summary: 'Change password' })
  @ApiResponse({ status: 200, description: 'Password changed successfully' })
  @ApiResponse({ status: 401, description: 'Current password incorrect' })
  async changePassword(
    @Req() req: FastifyRequest,
    @Body() changePasswordDto: ChangePasswordDto,
  ) {
    return this.userService.changePassword(req.user.id, changePasswordDto);
  }

  @Delete('account')
  @ApiOperation({ summary: 'Delete user account' })
  @ApiResponse({ status: 200, description: 'Account deleted successfully' })
  async deleteAccount(@Req() req: FastifyRequest) {
    return this.userService.deleteAccount(req.user.id);
  }

  @Get('users')
  @ApiOperation({ summary: 'Get user list (paginated)' })
  @ApiQuery({
    name: 'page',
    required: false,
    type: Number,
    description: 'Page number',
  })
  @ApiQuery({
    name: 'limit',
    required: false,
    type: Number,
    description: 'Items per page',
  })
  @ApiResponse({ status: 200, description: 'User list retrieved successfully' })
  async getUserList(
    @Req() req: FastifyRequest,
    @Query('page') page: number = 1,
    @Query('limit') limit: number = 10,
  ) {
    return this.userService.getUserList(
      req.user.id,
      Number(page),
      Number(limit),
    );
  }

  @Get('profile')
  @ApiOperation({ summary: 'Get current user profile' })
  @ApiResponse({
    status: 200,
    description: 'User profile retrieved',
    type: UserResponseDto,
  })
  async getUserProfile(
    @Req() req: FastifyRequest,
  ): Promise<ApiSuccessResponse<UserResponseDto>> {
    return this.userService.getUserProfile(req.user.id);
  }

  @Get('users/:id')
  @ApiOperation({ summary: 'Get user by ID' })
  @ApiResponse({
    status: 200,
    description: 'User retrieved successfully',
    type: UserResponseDto,
  })
  @ApiResponse({ status: 404, description: 'User not found' })
  async getUserById(
    @Param('id') id: number,
  ): Promise<ApiSuccessResponse<UserResponseDto>> {
    return this.userService.getUserById(id);
  }

  @Put('profile')
  @UseInterceptors(FileInterceptor('image_url'))
  @ApiConsumes('multipart/form-data')
  @ApiOperation({ summary: 'Update current user profile' })
  @ApiResponse({
    status: 200,
    description: 'Profile updated successfully',
    type: UserResponseDto,
  })
  async updateUser(
    @Req() req: FastifyRequest,
    @Body() updateUserDto: UpdateUserDto,
    @UploadedFile() image_url?: MulterFile,
  ): Promise<ApiSuccessResponse<UserResponseDto>> {
    return this.userService.updateUser(req.user.id, {
      ...updateUserDto,
      image_url,
    });
  }

  @Get('programs')
  @Public()
  @ApiOperation({ summary: 'List programs' })
  @ApiResponse({
    status: 200,
    description: 'Programs retrieved successfully',
  })
  async listPrograms() {
    return this.userService.listPrograms();
  }

  @Get('categories')
  @Public()
  @ApiOperation({ summary: 'List categories' })
  @ApiResponse({
    status: 200,
    description: 'Categories retrieved successfully',
  })
  async listCategories() {
    return this.userService.listCategories();
  }
}
